import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, MapPin, Ticket } from "lucide-react";
import SiteHeader from "../components/SiteHeader";
import { BlogFooter } from "../blog/BlogPages";
import { api, getUserId, isGiggleNull } from "../rsvp/api";
import { formatDayRange, formatStartTime } from "../rsvp/format";
import { eventHighlights } from "../rsvp/highlights";
import "../blog/blog.css";
import "../styles/EventsPage.css";

// Same formatting as the RSVP page: times are shown as stored (local event time), not shifted
// into the viewer's timezone.
const formatWhen = (e) => (e.date_tbd ? "Date to be announced" : `${formatDayRange(e.event_start_date, e.event_end_date)} · ${formatStartTime(e.event_start_date)}`);

/** Cheapest pass on sale (the host's ticket isn't one). */
const priceFrom = (event) => {
  const prices = (event.tickets || [])
    .filter((t) => t.is_active && t.ticket_name?.toLowerCase() !== "host")
    .map((t) => t.ticket_pricing ?? 0);
  return prices.length ? Math.min(...prices) : null;
};

const seatsLeft = (event) =>
  (event.tickets || [])
    .filter((t) => t.is_active && t.ticket_name?.toLowerCase() !== "host")
    // People, not passes: each group left on a group pass admits group_size people.
    .reduce((n, t) => n + Math.max(0, t.remaining_tickets ?? 0) * Math.max(1, t.group_size || 1), 0);

/** Every page of the public event list. */
export async function fetchAllEvents() {
  const all = [];
  for (let page = 1; page <= 20; page++) {
    const res = await api.events(page, getUserId());
    const list = res.data?.data || [];
    all.push(...list);
    if (list.length === 0 || all.length >= (res.data?.total_count ?? 0)) break;
  }
  return all;
}

/** /events: upcoming public events, each linking to its RSVP page. */
const EventsPage = () => {
  const [events, setEvents] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    document.title = "Events · Blox";
    window.scrollTo(0, 0);
    (async () => {
      try {
        const now = Date.now();
        const upcoming = (await fetchAllEvents())
          .filter((e) => e.is_active && !e.private_event && !e.is_expired && new Date(e.event_end_date).getTime() > now)
          .sort((a, b) => new Date(a.event_start_date) - new Date(b.event_start_date));
        // RSVP pages take an encrypted event id in the link.
        const withLinks = await Promise.all(upcoming.map(async (e) => {
          try { return { ...e, code: (await api.encrypt(String(e.id))).data?.encrypt_string }; }
          catch { return { ...e, code: null }; }
        }));
        setEvents(withLinks.filter((e) => e.code));
      } catch (e) {
        console.error("Couldn't load events:", e);
        setError(true);
      }
    })();
  }, []);

  return (
    <main className="blog-page">
      <SiteHeader />
      <section className="blog-hero">
        <p className="blog-eyebrow">What's happening</p>
        <h1>Events</h1>
        <p className="blog-lede">Meetups, parties and campus moments near you. Book a pass in a few taps.</p>
      </section>

      <section className="blog-list" aria-live="polite">
        {error ? (
          <p className="events-empty">We couldn't load events right now. Please refresh in a moment.</p>
        ) : events === null ? (
          <div className="blog-grid">
            {[0, 1, 2].map((i) => <div key={i} className="blog-card events-skeleton" />)}
          </div>
        ) : events.length === 0 ? (
          <p className="events-empty">No upcoming events right now. Check back soon, or open the Blox app to see what's on.</p>
        ) : (
          <div className="blog-grid">
            {events.map((e, i) => {
              const from = priceFrom(e);
              const left = seatsLeft(e);
              const closed = e.last_date && new Date(e.last_date).getTime() < Date.now();
              const venue = e.venue_tbd ? "Venue to be announced" : [e.event_venue, isGiggleNull(e.event_address) ? "" : e.event_address].filter(Boolean)[0];
              return (
                <Link to={`/event/${e.code}`} className="blog-card" key={e.id} style={{ animationDelay: `${i * 60}ms` }}>
                  <div className="events-banner">
                    {e.event_banner ? <img src={e.event_banner} alt="" loading="lazy" /> : <span aria-hidden="true">🎟️</span>}
                    {eventHighlights(e).length > 0 && (
                      <span className="events-highlights">
                        {eventHighlights(e).map((h) => <span key={h.label} className={`events-highlight events-highlight-${h.tone}`}>{h.label}</span>)}
                      </span>
                    )}
                    <span className="events-price">{from === null ? "" : from === 0 ? "Free" : `From ₹${from}`}</span>
                  </div>
                  <div className="blog-card-body">
                    <p className="blog-meta">
                      {e.event_category?.category_name && <span className="blog-chip">{e.event_category.category_name}</span>}
                      <span className={closed || left === 0 ? "events-status events-status-closed" : "events-status"}>
                        {closed ? "Registration closed" : left === 0 ? "Full · waitlist open" : `${left} spots left`}
                      </span>
                    </p>
                    <h3>{e.event_name}</h3>
                    <p className="events-fact"><CalendarDays size={15} /> {formatWhen(e)}</p>
                    {venue && <p className="events-fact"><MapPin size={15} /> {venue}</p>}
                    <span className="blog-read events-cta"><Ticket size={15} /> {closed || left === 0 ? "View event" : "RSVP now"} →</span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>
      <BlogFooter />
    </main>
  );
};

export default EventsPage;
